import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { capitalize } from "lodash";
import { Box, Stack, Typography } from "@mui/material";
import { AttributeProductFragment, CountryCode, ProductPageProductFragment } from "../../generated/storefront";
import Button from "../../components/Button";
import { colors, fonts } from "../../theme";
import useBasketActions from "../../hooks/useBasketActions";
import { formattedPrice } from "../../helpers/money";
import SelectButton from "./SelectButton";
import { sizeLabelsForCountry } from "../../helpers/product";
import { Size } from "../../types/product";
import QualitySection from "./QualitySection";

type Props = {
  product: ProductPageProductFragment;
  isMobileScreen: boolean;
  navHeight: number;
  selectedCountry: CountryCode;
  premiumFrameProduct?: AttributeProductFragment | null;
};

const disclaimerStyles = {
  fontSize: 10,
  lineHeight: 1,
  color: "primary",
  fontFamily: fonts.body,
  paddingX: 0.5,
  paddingTop: 0.4,
  borderRadius: 1,
};

const StandardProduct = ({ product, isMobileScreen, navHeight, selectedCountry, premiumFrameProduct }: Props) => {
  const [state, setState] = useState<Record<string, string>>({});
  const { addOrCreateBasket, loading, error } = useBasketActions();
  const variants = product.variants.nodes;
  const productOptions = product.options;
  const reversedProductOptions = useMemo(() => [...productOptions].reverse(), [productOptions]);
  const options = useMemo(
    () => (isMobileScreen ? reversedProductOptions : productOptions),
    [isMobileScreen, productOptions, reversedProductOptions]
  );
  const selectedVariant = useMemo(
    () =>
      variants.find((variant) =>
        variant.selectedOptions.every((option) => {
          return state[option.name] === option.value;
        })
      ) || variants[0],
    [variants, state]
  );
  const sizeLabels = sizeLabelsForCountry(selectedCountry);
  const image = useMemo(() => selectedVariant.image || product.images.nodes[0], [selectedVariant, product.images.nodes]);

  const handleChange = (event: any) => {
    const { name, value } = event.target;
    setState({ ...state, [name]: value });
  };

  const addToBasket = () => {
    if (!selectedVariant) return;
    return addOrCreateBasket([
      {
        type: "standard" as const,
        merchandiseId: selectedVariant.id,
        quantity: 1,
      },
    ]);
  };

  const getSelectedOption = (optionName: string) => {
    if (optionName === "size") return state.size ? sizeLabels[state.size as Size].framedMeasurements : "";
    if (product.isGiftCard) return "";
    return capitalize(state[optionName]);
  };

  const getOptionLabel = (optionValue: { id: string; name: string }) => {
    if (product.isGiftCard) {
      const variant = variants.find((variant) => variant.selectedOptions[0].value === optionValue.name);
      if (!variant) return optionValue.name;
      return formattedPrice(variant.price.amount, variant.price.currencyCode);
    }
    return optionValue.name;
  };

  useEffect(() => {
    if (product.options.length > 0) {
      const initialState = product.options.reduce((acc, option) => {
        const selectedOption = option.optionValues[0];
        acc[option.name] = selectedOption.name;
        return acc;
      }, {} as Record<string, string>);
      setState(initialState);
    }
  }, [product.options]);

  return (
    <>
      <Helmet>
        <title>{product.title} | GoodMood Prints</title>
        <meta name="description" content={product.description} />
      </Helmet>
      <Stack direction={{ xs: "column", md: "row" }}>
        {isMobileScreen ? (
          <Box height="50vh" bgcolor={colors.cardGrey}>
            <img
              src={image.src + "&width=500"}
              alt={image.altText || product.title}
              width="100%"
              height="100%"
              style={{ objectFit: "contain" }}
            />
          </Box>
        ) : (
          <Stack width="67%" height={`calc(100vh - ${navHeight}px)`} gap={0.2} bgcolor={colors.cardGrey}>
            <img
              src={image.src + "&width=800"}
              alt={image.altText || product.title}
              width="100%"
              height="100%"
              style={{ objectFit: "contain" }}
            />
          </Stack>
        )}
        <Stack width={{ xs: "100%", md: "33%" }}>
          <Stack gap={1} height="100%">
            <Box paddingX={{ xs: 2, md: 3 }} paddingTop={{ xs: 1.5, md: 0 }}>
              <Stack justifyContent="space-between" direction="row" gap={3}>
                {isMobileScreen ? (
                  <Typography component="h1">{product.title}</Typography>
                ) : (
                  <Stack gap={1} direction="row" alignItems="center">
                    <Typography variant="h2" component="h1">
                      {product.title}
                    </Typography>
                  </Stack>
                )}
                <Typography fontSize={{ xs: 14, md: 16 }} style={{ whiteSpace: "nowrap" }} align="right">
                  {formattedPrice(selectedVariant.price.amount, selectedVariant.price.currencyCode)}
                </Typography>
              </Stack>
            </Box>
            <Box position="sticky">
              <Box paddingX={{ xs: 2, md: 3 }}>
                <Stack gap={2}>
                  {options.map((option) => (
                    <Stack gap={1}>
                      <Typography fontSize={12}>
                        {capitalize(option.name)}: {getSelectedOption(option.name)}
                      </Typography>
                      <Stack onChange={handleChange} direction="row" flexWrap="wrap" gap={1}>
                        {option.optionValues.map((optionValue) => (
                          <SelectButton
                            type="radio"
                            name={option.name}
                            key={optionValue.id}
                            id={optionValue.id}
                            label={getOptionLabel(optionValue)}
                            value={optionValue.name}
                            image={optionValue.swatch?.image?.previewImage?.url}
                            checked={state[option.name] === optionValue.name}
                            disclaimer={
                              optionValue.swatch?.image?.previewImage?.url ? (
                                <Typography bgcolor={colors.white} {...disclaimerStyles}>
                                  {capitalize(optionValue.name)}
                                </Typography>
                              ) : undefined
                            }
                          />
                        ))}
                      </Stack>
                    </Stack>
                  ))}

                  <Stack paddingY={2} gap={1.5}>
                    <Button
                      fullWidth
                      onClick={addToBasket}
                      disabled={!Object.values(state).every((v) => v)}
                      loading={loading}
                      size="large"
                    >
                      Add to cart
                    </Button>
                    {!product.isGiftCard && (
                      <Typography variant="caption" align="center" color="text.secondary" fontSize={{ xs: 12, md: 14 }}>
                        No import fees for UK, EU, AUS & USA
                      </Typography>
                    )}
                    {error && <Typography color={colors.error}>{error}</Typography>}
                  </Stack>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Stack>
      </Stack>
      <Stack gap={{ xs: 6, md: 9 }} paddingY={{ xs: 6, md: 9 }}>
        <QualitySection isMobileScreen={isMobileScreen} selectedCountry={selectedCountry} />
      </Stack>
    </>
  );
};

export default StandardProduct;
